import styled from 'styled-components';

// O `loader.css` do viewer zera padding/margin de tudo dentro de `.app-shell` e
// carrega depois destes estilos: na mesma especificidade ele vence. O `&&`
// dobra a classe para que espaçamento declarado aqui valha de fato.

export const ToolbarContainer = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  z-index: 1000;
`;

export const Pill = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  gap: 4px;

  && {
    padding: 4px;
  }
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e8e8e8;
`;

/**
 * Âncora da pílula principal: o que se posiciona em relação à barra (o progresso
 * do data model) fica absoluto aqui dentro, sem empurrar a toolbar.
 */
export const MainPillAnchor = styled.div`
  position: relative;
`;

/**
 * Acima e à esquerda da toolbar: o fim fica 36px antes do início dela e a base
 * da barra 8px acima dela. O conteúdo tem 5px de área de hover embaixo, por
 * isso 3px aqui.
 */
export const ProgressSlot = styled.div`
  position: absolute;
  right: calc(100% + 36px);
  bottom: calc(100% + 3px);
`;

/** Âncora do submenu: o grupo abre acima do próprio botão, não no centro. */
export const ToolAnchor = styled.div`
  position: relative;
  display: flex;
`;

export const GroupPopover = styled(Pill)`
  position: absolute;
  bottom: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
`;

export const Divider = styled.span`
  width: 1px;
  height: 20px;

  && {
    margin: 0 4px;
  }
  background: #e8e8e8;
`;

/**
 * 32px quadrado; o botão de grupo é mais largo para caber o chevron. Ativo =
 * preenchido; grupo aberto = só a borda (o preenchimento fica com a ferramenta
 * escolhida lá dentro).
 */
export const ToolbarButton = styled.button<{ $active?: boolean; $group?: boolean; $open?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: ${({ $group }) => ($group ? '52px' : '32px')};
  height: 32px;
  padding: 0;
  border: 1px solid ${({ $open, theme }) => ($open ? theme.colors.surface.surfaceOutline : 'transparent')};
  border-radius: 6px;
  background: ${({ $active, theme }) => ($active
    ? `color-mix(in srgb, ${theme.colors.primary.primaryContainer}, ${theme.colors.surface.surface})`
    : 'transparent')};
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: ${({ $active, theme }) => ($active
    ? `color-mix(in srgb, ${theme.colors.primary.primaryPressed}, ${theme.colors.surface.surface})`
    : theme.colors.surface.surfaceContainer)};
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;

    &:hover {
      background: transparent;
    }
  }
`;

export const SectionPanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 360px;

  && {
    padding: 14px;
  }
  background: ${({ theme }) => theme.colors.surface.surface};
  border: 1px solid ${({ theme }) => theme.colors.surface.surfaceOutline};
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surface.surfaceOutline};

  && {
    padding-bottom: 12px;
  }

  .sectionTitle {
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.surface.onSurface};
  }
`;

export const SectionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .sectionLabel {
    flex-shrink: 0;
    width: 64px;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.surface.onSurface};
  }

  /*
   * A barra e a bolinha do slider do antd são posicionadas por caminhos
   * diferentes: a barra é absoluta SEM top (cai no static position, que depende
   * do padding-block do slider) e a bolinha vem de um inset-block-start fixo.
   * Qualquer mudança na altura do slider — esticar como flex item, por exemplo —
   * separa os dois: a barra sobe e a bolinha fica no lugar. Ancorar ambos no
   * meio tira a dependência.
   *
   * A bolinha não pode ir por transform: o rc-slider escreve translateX(-50%)
   * inline (é como ele posiciona o valor na horizontal) e o inline vence — daí a
   * margem negativa de meia bolinha.
   */
  .sectionSlider {
    --handle-size: 10px;
    flex: 1;
    min-width: 0;

    .ant-slider-rail,
    .ant-slider-track {
      top: 50%;
      transform: translateY(-50%);
    }

    .ant-slider-handle {
      inset-block-start: 50%;
      margin-block-start: calc(var(--handle-size) / -2);
    }
  }

  .sectionValue {
    flex-shrink: 0;
    width: 40px;
    font-size: 13px;
    text-align: right;
    color: ${({ theme }) => theme.colors.surface.onSurface};
  }
`;

/** Controle segmentado dos eixos: trilho cinza, o ativo vira a "aba" branca. */
export const AxisGroup = styled.div`
  display: flex;
  gap: 4px;
  border-radius: 8px;

  && {
    padding: 4px;
  }
  background: ${({ theme }) => theme.colors.secondary.secondary};
`;

export const AxisButton = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: ${({ $active, theme }) => ($active ? theme.colors.surface.surface : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? '0 1px 2px rgba(0, 0, 0, 0.08)' : 'none')};
  color: ${({ theme }) => theme.colors.surface.onSurface};
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s;
`;
